import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { getAllSchedules, getAllTutors, getScheduleMemos, subscribeToMemos } from '../services/firestoreService';
import '../styles/DataManagement.css';

const DataManagement = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalSchedules: 0,
    pendingSchedules: 0,
    pendingWithMemoSchedules: 0,
    confirmedSchedules: 0,
    registeredSchedules: 0,
    totalTutors: 0,
    totalMemos: 0
  });

  // 통계 정보 로드
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [schedules, tutors] = await Promise.all([
        getAllSchedules(),
        getAllTutors()
      ]);

      const pendingCount = schedules.filter(s => s.status === 'pending').length;
      const pendingWithMemoCount = schedules.filter(s => s.status === 'pending_with_memo').length;
      const confirmedCount = schedules.filter(s => s.status === 'confirmed').length;
      const registeredCount = schedules.filter(s => s.status === 'registered').length;

      // 메모 개수 계산
      const registeredSchedules = schedules.filter(s => s.status === 'registered');
      const memoPromises = registeredSchedules.map(schedule => getScheduleMemos(schedule.id));
      const memoResults = await Promise.all(memoPromises);
      const totalMemos = memoResults.reduce((total, memos) => total + memos.length, 0);

      setStats({
        totalSchedules: schedules.length,
        pendingSchedules: pendingCount,
        pendingWithMemoSchedules: pendingWithMemoCount,
        confirmedSchedules: confirmedCount,
        registeredSchedules: registeredCount,
        totalTutors: tutors.length,
        totalMemos
      });
    } catch (error) {
      console.error('통계 로드 오류:', error);
    }
  };

  const handleBackupDownload = async () => {
    setLoading(true);
    try {
      console.log('📊 엑셀 다운로드 시작...');
      
      // 모든 데이터 가져오기
      const [schedules, tutors] = await Promise.all([
        getAllSchedules(),
        getAllTutors()
      ]);

      // 모든 메모 데이터 가져오기 (Promise로 한 번에 가져오기)
      const allMemos = await new Promise((resolve, reject) => {
        const unsubscribe = subscribeToMemos((memosData) => {
          unsubscribe(); // 즉시 구독 해제
          resolve(memosData);
        });
        // 3초 내에 응답이 없으면 타임아웃
        setTimeout(() => {
          unsubscribe();
          reject(new Error('메모 데이터 로드 타임아웃'));
        }, 3000);
      });

      // 메모 맵 생성
      const memoMap = new Map();
      
      // 1. registered 스케줄의 기존 메모들
      const registeredSchedules = schedules.filter(s => s.status === 'registered');
      const memoPromises = registeredSchedules.map(async (schedule) => {
        try {
          const memos = await getScheduleMemos(schedule.id);
          return { scheduleId: schedule.id, memoInfo: memos.length > 0 ? memos[0] : null };
        } catch (error) {
          console.error(`메모 정보 로드 오류 (스케줄 ${schedule.id}):`, error);
          return { scheduleId: schedule.id, memoInfo: null };
        }
      });
      
      const memoResults = await Promise.all(memoPromises);
      memoResults.forEach(({ scheduleId, memoInfo }) => {
        if (memoInfo) {
          memoMap.set(scheduleId, memoInfo);
        }
      });

      // 2. pending_with_memo 스케줄의 pending_memo 메모들
      const pendingWithMemoSchedules = schedules.filter(s => s.status === 'pending_with_memo');
      pendingWithMemoSchedules.forEach(schedule => {
        const pendingMemo = allMemos.find(memo => 
          memo.memoType === 'pending_memo' &&
          memo.tutorId === schedule.tutorId &&
          memo.dayOfWeek === schedule.dayOfWeek &&
          (memo.timeSlot === schedule.timeSlot || memo.time === schedule.timeSlot)
        );
        
        if (pendingMemo) {
          // pending_memo를 기존 메모 형식에 맞게 변환
          const convertedMemo = {
            student: '매칭 대기',
            reason: pendingMemo.reason || '기타',
            memo: pendingMemo.memo || '',
            tutorName: pendingMemo.tutorName || '',
            timestamp: pendingMemo.createdAt || pendingMemo.timestamp,
            unavailableDates: pendingMemo.unavailableDates || []
          };
          memoMap.set(schedule.id, convertedMemo);
        }
      });

      // 엑셀 데이터 준비
      console.log('🔍 디버깅 정보:');
      console.log('튜터 데이터:', tutors);
      console.log('스케줄 데이터:', schedules);
      
      const excelData = schedules.map(schedule => {
        const memoInfo = memoMap.get(schedule.id);
        const tutor = tutors.find(t => (t.email || '').toLowerCase() === (schedule.tutorId || '').toLowerCase() || t.userId === schedule.tutorId);
        
        console.log(`스케줄 ${schedule.id}: tutorId=${schedule.tutorId}, 찾은 튜터=`, tutor);
        
        return {
          '요일': getDayText(schedule.dayOfWeek),
          '시간대': schedule.timeSlot,
          '튜터명': tutor ? tutor.name : '알 수 없음',
          '튜터ID': schedule.tutorId,
          '상태': getStatusText(schedule.status),
          '학생명': schedule.studentName || (memoInfo ? memoInfo.student : ''),
          '신청일시': formatTimestamp(schedule.requestedAt || schedule.createdAt),
          '확정일시': formatTimestamp(schedule.confirmedAt),
          '메모사유': memoInfo ? getReasonText(memoInfo.reason) : '',
          '메모내용': memoInfo ? memoInfo.memo : '',
          '메모작성자': memoInfo ? memoInfo.tutorName : '',
          '메모작성일시': formatTimestamp(memoInfo?.timestamp),
          '불가능날짜': memoInfo && memoInfo.unavailableDates ? memoInfo.unavailableDates.join(', ') : ''
        };
      });

      // 요일별로 정렬
      const dayOrder = ['월요일', '화요일', '수요일', '목요일', '금요일', '토요일', '일요일'];
      excelData.sort((a, b) => {
        const dayA = dayOrder.indexOf(a['요일']);
        const dayB = dayOrder.indexOf(b['요일']);
        if (dayA !== dayB) return dayA - dayB;
        return a['시간대'].localeCompare(b['시간대']);
      });

      // 워크북 생성
      const wb = XLSX.utils.book_new();
      
      // 요일별로 데이터 분리
      const dayDataMap = {};
      
      // 각 요일별로 데이터 그룹화
      dayOrder.forEach(day => {
        dayDataMap[day] = excelData.filter(item => item['요일'] === day);
      });
      
      // 요일별 시트 생성
      dayOrder.forEach(day => {
        const dayData = dayDataMap[day];
        if (dayData.length > 0) {
          const ws = XLSX.utils.json_to_sheet(dayData);
          XLSX.utils.book_append_sheet(wb, ws, day);
        }
      });

      // 통계 시트
      const statsData = [
        { '구분': '총 스케줄 수', '개수': stats.totalSchedules },
        { '구분': '매칭 대기', '개수': stats.pendingSchedules },
        { '구분': '매칭 대기 메모', '개수': stats.pendingWithMemoSchedules },
        { '구분': '확정 완료', '개수': stats.confirmedSchedules },
        { '구분': '메모 등록', '개수': stats.registeredSchedules },
        { '구분': '총 튜터 수', '개수': stats.totalTutors },
        { '구분': '총 메모 수', '개수': stats.totalMemos }
      ];
      const statsWs = XLSX.utils.json_to_sheet(statsData);
      XLSX.utils.book_append_sheet(wb, statsWs, '통계');

      // 파일명 생성 (현재 날짜 포함)
      const now = new Date();
      const fileName = `코더랜드_스케줄_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}.xlsx`;

      // 파일 다운로드
      XLSX.writeFile(wb, fileName);
      
      console.log('✅ 엑셀 다운로드 완료:', fileName);
      
    } catch (error) {
      console.error('엑셀 다운로드 오류:', error);
      alert('❌ 엑셀 다운로드에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  // 요일 텍스트 변환
  const getDayText = (dayOfWeek) => {
    const dayMap = {
      'monday': '월요일',
      'tuesday': '화요일',
      'wednesday': '수요일',
      'thursday': '목요일',
      'friday': '금요일',
      'saturday': '토요일',
      'sunday': '일요일'
    };
    return dayMap[dayOfWeek] || dayOfWeek;
  };

  // 상태 텍스트 변환
  const getStatusText = (status) => {
    const statusMap = {
      'pending': '매칭 대기',
      'pending_with_memo': '매칭 대기 메모',
      'confirmed': '확정 완료',
      'registered': '메모 등록'
    };
    return statusMap[status] || status;
  };

  // 사유 텍스트 변환
  const getReasonText = (reason) => {
    const reasonMap = {
      'schedule_conflict': '스케줄 충돌',
      'student_request': '학생 요청',
      'personal_matter': '개인 사정',
      'emergency': '긴급 상황',
      'system_error': '시스템 오류',
      'other': '기타'
    };
    return reasonMap[reason] || '기타';
  };

  // 타임스탬프 포맷팅
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('ko-KR');
    } catch (error) {
      return '';
    }
  };

  return (
    <div className="data-management">
      <div className="data-management-header">
        <h2>💾 데이터 관리</h2>
        <p>현재 튜터 스케줄 데이터를 엑셀로 백업합니다</p>
      </div>

      <div className="data-management-content">
        {/* 통계 정보 */}
        <div className="stats-section">
          <h3>📊 현재 데이터 현황</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">{stats.totalSchedules}</div>
              <div className="stat-label">총 스케줄</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{stats.pendingSchedules}</div>
              <div className="stat-label">매칭 대기</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{stats.pendingWithMemoSchedules}</div>
              <div className="stat-label">매칭 대기 메모</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{stats.confirmedSchedules}</div>
              <div className="stat-label">확정 완료</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{stats.registeredSchedules}</div>
              <div className="stat-label">메모 등록</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{stats.totalTutors}</div>
              <div className="stat-label">총 튜터</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">{stats.totalMemos}</div>
              <div className="stat-label">총 메모</div>
            </div>
          </div>
        </div>

        {/* 백업 관리 섹션 */}
        <div className="backup-section">
          <div className="backup-info">
            <div className="backup-icon">📊</div>
            <h3>스케줄 데이터 백업</h3>
            <p>현재 등록된 모든 튜터의 스케줄 데이터를 엑셀 파일로 다운로드합니다.</p>
            <div className="backup-details">
              <h4>📋 백업 포함 데이터:</h4>
              <ul>
                <li>📅 요일별 시트 분리 (월요일~일요일)</li>
                <li>⏰ 시간대별 스케줄 정보</li>
                <li>👨‍🏫 튜터 정보 (이름, ID)</li>
                <li>📝 스케줄 상태 (매칭대기/매칭대기메모/확정완료/메모등록)</li>
                <li>👨‍🎓 매칭된 학생 정보</li>
                <li>📝 메모 정보 (사유, 내용, 작성자, 작성일시)</li>
                <li>🚫 불가능 날짜 정보 (매칭대기메모 상태)</li>
                <li>📊 통계 정보 (별도 시트)</li>
              </ul>
            </div>
          </div>

          <div className="backup-actions">
            <button 
              className="backup-btn"
              onClick={handleBackupDownload}
              disabled={loading}
            >
              {loading ? '📥 다운로드 중...' : '📥 엑셀 다운로드'}
            </button>
            <p className="backup-note">
              💡 서버 다운 시를 대비해 정기적으로 백업을 다운로드하세요.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataManagement; 